import { useTranslation } from 'react-i18next'
import { Truck, Tag, ShoppingCart, TrendingUp, BarChart3 } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'
import { SectionCard } from './shared/SectionCard'
import { UnitEconomics } from './UnitEconomics'
import { RecommendationCards } from './shared/RecommendationCards'

interface PricingAnalysisProps {
  analysis: AuditReport['pricing_analysis']
}

export function PricingAnalysis({ analysis }: PricingAnalysisProps) {
  const { t } = useTranslation()
  if (!analysis) return null
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const pp = analysis.price_position || {}

  return (
    <>
      <SectionCard title={r('pricingAnalysis')} score={analysis.score}>
        <PriceBar min={pp.market_min} max={pp.market_max} avg={pp.market_avg} source={pp.source_price} r={r} percentile={pp.percentile} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <StatCard label={r('primeStatus')} value={analysis.prime_analysis?.source_is_prime ? r('prime') : r('standard')} icon={<Truck className="w-4 h-4" />} highlight={analysis.prime_analysis?.source_is_prime} />
          <StatCard label={r('couponStrategy')} value={analysis.coupon_analysis?.source_has_coupon ? r('active') : r('inactive')} icon={<Tag className="w-4 h-4" />} />
          <StatCard label={r('buyBox')} value={analysis.buy_box?.eligible ? r('eligible') : r('atRisk')} icon={<ShoppingCart className="w-4 h-4" />} highlight={analysis.buy_box?.eligible} />
        </div>
      </SectionCard>

      {analysis.value_analysis?.perceived_value && <ValueAnalysisCard va={analysis.value_analysis} r={r} />}
      {analysis.bsr_analysis?.source_bsr && <BsrCard bsr={analysis.bsr_analysis} r={r} />}
      {analysis.unit_economics && <UnitEconomics data={analysis.unit_economics} />}
      {analysis.recommendations?.length > 0 && <RecommendationCards recs={analysis.recommendations} t={t} />}
    </>
  )
}

function PriceBar({ min, max, avg, source, r, percentile }: {
  min?: number; max?: number; avg?: number; source?: number; r: (k: string) => string; percentile?: number
}) {
  if (min == null || max == null || source == null) return null
  const currency = '€'
  const range = max - min || 1
  const sourcePos = Math.min(Math.max(((source - min) / range) * 100, 2), 98)
  const avgPos = avg ? Math.min(Math.max(((avg - min) / range) * 100, 0), 100) : null

  const pctLabel = percentile != null ? pricePositionLabel(percentile, r) : null

  return (
    <div className="space-y-2">
      {/* Source marker with arrow */}
      <div className="relative h-10" style={{ marginLeft: '8px', marginRight: '8px' }}>
        <div className="absolute flex flex-col items-center -translate-x-1/2" style={{ left: `${sourcePos}%`, top: 0 }}>
          <span className="px-2.5 py-1 bg-orange-500 text-white rounded-md text-xs font-bold shadow-md ring-2 ring-orange-300">
            {currency}{source.toFixed(2)}
          </span>
          <span className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-orange-500" />
        </div>
      </div>

      {/* Bar */}
      <div className="relative h-3 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-700" style={{ marginLeft: '8px', marginRight: '8px' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-400 via-primary-200 to-primary-400 dark:from-primary-800 dark:via-primary-600 dark:to-primary-800" />
        {/* Source position line */}
        <div className="absolute top-0 h-full w-1 bg-orange-500 rounded-full -translate-x-1/2 z-10" style={{ left: `${sourcePos}%` }} />
        {/* Avg line */}
        {avgPos != null && (
          <div className="absolute top-0 h-full w-0.5 bg-neutral-600 dark:bg-neutral-300 -translate-x-1/2" style={{ left: `${avgPos}%` }} />
        )}
      </div>

      {/* Labels */}
      <div className="flex flex-wrap justify-between gap-1 text-2xs sm:text-xs text-neutral-500 px-2">
        <span>{r('priceCheapest')}: {currency}{min.toFixed(2)}</span>
        {avg != null && (
          <span className="text-neutral-600 dark:text-neutral-400">ø {currency}{avg.toFixed(2)}</span>
        )}
        <span>{r('priceExpensive')}: {currency}{max.toFixed(2)}</span>
      </div>

      {/* Percentile explanation */}
      {pctLabel && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">{pctLabel}</p>
      )}
    </div>
  )
}

function pricePositionLabel(pct: number, r: (k: string) => string): string {
  if (pct <= 20) return r('pricePctVeryLow')
  if (pct <= 40) return r('pricePctLow')
  if (pct <= 60) return r('pricePctMiddle')
  if (pct <= 80) return r('pricePctHigh')
  return r('pricePctVeryHigh')
}

function ValueAnalysisCard({ va, r }: { va: AuditReport['pricing_analysis']['value_analysis']; r: (k: string) => string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
        <TrendingUp className="w-4 h-4 text-neutral-500" /> {r('valueAnalysis')}
      </h4>
      <div className="flex flex-wrap gap-2 mb-2">
        <Badge label={r('perceivedValue')} value={va.perceived_value} />
        <Badge label={r('priceQualityRatio')} value={va.price_quality_ratio} />
      </div>
      {va.detail && <p className="text-sm text-neutral-600 dark:text-neutral-400">{va.detail}</p>}
    </div>
  )
}

function BsrCard({ bsr, r }: { bsr: NonNullable<AuditReport['pricing_analysis']['bsr_analysis']>; r: (k: string) => string }) {
  const ranks = Array.isArray(bsr.source_bsr) ? bsr.source_bsr : [bsr.source_bsr]
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
      <h4 className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
        <BarChart3 className="w-4 h-4 text-neutral-500" /> {r('bsrAnalysis')}
      </h4>
      <div className="space-y-2 mb-3">
        {ranks.map((rank, i) => (
          <div key={i} className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-3 flex items-center gap-3">
            <span className="text-xs text-neutral-500 flex-shrink-0">BSR {ranks.length > 1 ? `#${i + 1}` : ''}</span>
            <span className="text-sm font-bold text-neutral-900 dark:text-white">{rank}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        <Badge label={r('relativePosition')} value={bsr.relative_position} />
        {bsr.competitor_bsr_range && bsr.competitor_bsr_range.best !== 'unknown' && (
          <Badge label={r('bsrRange')} value={`${bsr.competitor_bsr_range.best} – ${bsr.competitor_bsr_range.worst}`} />
        )}
      </div>
      {bsr.detail && <p className="text-sm text-neutral-600 dark:text-neutral-400">{bsr.detail}</p>}
    </div>
  )
}

const VALUE_LABELS: Record<string, string> = {
  low: 'Niedrig', 'low to medium': 'Niedrig bis Mittel', medium: 'Mittel',
  'medium to high': 'Mittel bis Hoch', high: 'Hoch',
  poor: 'Schlecht', below_average: 'Unterdurchschnittlich', average: 'Durchschnittlich',
  above_average: 'Überdurchschnittlich', excellent: 'Hervorragend',
  clear: 'Klar', vague: 'Unklar', missing: 'Fehlt',
  unknown: 'Unbekannt', unclear: 'Unklar',
  budget: 'Budget', 'entry-level': 'Einstieg', 'mid-range': 'Mittelklasse', premium: 'Premium', luxury: 'Luxus',
}

function Badge({ label, value }: { label: string; value: string }) {
  if (!value || value === 'unknown') return null
  const display = VALUE_LABELS[value?.toLowerCase()] ?? value?.replace(/_/g, ' ')
  return (
    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
      {label}: {display}
    </span>
  )
}

function StatCard({ label, value, icon, highlight }: { label: string; value: string; icon: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`rounded-lg p-3 text-center ${highlight ? 'bg-green-50 dark:bg-green-900/20' : 'bg-neutral-50 dark:bg-neutral-700/50'}`}>
      <div className="text-neutral-500 flex justify-center mb-1">{icon}</div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-sm font-bold text-neutral-900 dark:text-white mt-0.5">{value}</div>
    </div>
  )
}
